import React, { useState, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Divider,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  alpha,
  Badge,
  Chip,
  Stack,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import { useTheme } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import logoWhite from "../../theme/logo/logo_white.png";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CircleIcon from "@mui/icons-material/Circle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import { TAX_SAVY_STATIC_URL } from "../../utils/static/url";
import PublicIcon from "@mui/icons-material/Public";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import PaymentIcon from "@mui/icons-material/Payment";
import TuneIcon from "@mui/icons-material/Tune";

const mockNotifications = [
  {
    id: 1,
    title: "Welcome!",
    content: "Thank you for joining Tax Savy!",
    date: "2024-07-01 10:00",
    isRead: false,
    type: "info",
  },
  {
    id: 2,
    title: "Subscription Expiring",
    content: "Your subscription will expire in 3 days.",
    date: "2024-07-02 09:30",
    isRead: false,
    type: "warning",
  },
  {
    id: 3,
    title: "New version released!",
    content: "We have upgraded the app to make it perfect.",
    date: "2024-06-30 15:20",
    isRead: true,
    type: "update",
  },
];

const Header = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profileMenuOpen = Boolean(anchorEl);

  const { global_authState, logout } = useContext(AuthContext);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState(mockNotifications);
  const notificationMenuOpen = Boolean(notificationAnchorEl);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    // Mark all as read when menu opens
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };
  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const getUserTypeLabel = (userType, userRole) => {
    if (userType === "admin") return "Owner";
    if (userType === "user" && userRole === "admin") return "Admin User";
    if (userType === "user" && userRole === "editor") return "Editor";
    if (userType === "user" && userRole === "viewer") return "Viewer";
    if (userType === "guest") return "Guest";
    return "User";
  };

  const getUserTypeColor = (userType, userRole) => {
    if (userType === "admin") return "error";
    if (userType === "user" && userRole === "admin") return "warning";
    if (userType === "user" && userRole === "editor") return "info";
    if (userType === "user" && userRole === "viewer") return "success";
    return "default";
  };

  const getUserTypeIcon = (userType, userRole) => {
    if (userType === "admin") return <AdminPanelSettingsIcon />;
    if (userType === "user" && userRole === "admin")
      return <SupervisorAccountIcon />;
    return <PersonIcon />;
  };

  const navigationItems = [
    {
      label: "Console Panel",
      path: "/consolePanel",
      icon: <DashboardIcon />,
      external: false,
    },
    {
      label: "Website",
      path: TAX_SAVY_STATIC_URL,
      icon: <PublicIcon />,
      external: true,
    },
  ];

  return (
    <AppBar
      position='static'
      sx={{
        background: "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
        height: { xs: "64px", sm: "70px" },
        display: "flex",
        justifyContent: "center",
        padding: { xs: "0 8px", sm: "0 16px", md: "0 24px" },
        boxShadow: "0px 4px 20px rgba(96, 63, 151, 0.3)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          maxWidth: "1800px",
          mx: "auto",
          minHeight: { xs: "64px", sm: "70px" },
        }}
      >
        {/* Logo and Navigation */}
        <Box display='flex' alignItems='center' sx={{ flex: 1 }}>
          <Tooltip title='Navigate to Console Panel'>
            <img
              src={logoWhite}
              alt='Tax Savy Logo'
              onClick={() => navigate(isAuthenticated ? "/consolePanel" : "/")}
              style={{
                cursor: "pointer",
                width: isSmallMobile ? "150px" : "200px",
                height: "auto",
                filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                marginRight: isMobile ? "16px" : "50px",
                transition: "transform 0.2s ease-in-out",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
              }}
              tabIndex={0}
              aria-label='Navigate to homepage'
            />
          </Tooltip>

          {/* Desktop Navigation */}
          {isAuthenticated && !isMobile && (
            <Stack direction='row' spacing={1}>
              {navigationItems.map((item) => (
                <Tooltip key={item.label} title={item.label}>
                  <Button
                    variant='text'
                    onClick={() =>
                      item.external
                        ? window.open(item.path, "_blank")
                        : navigate(item.path)
                    }
                    startIcon={item.icon}
                    sx={{
                      color: "white",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        transform: "translateY(-1px)",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                    aria-label={`Navigate to ${item.label}`}
                  >
                    {item.label}
                  </Button>
                </Tooltip>
              ))}
              {process.env.REACT_APP_RENDER_ENV === "DEV" && (
                <Chip
                  label='Development'
                  color='warning'
                  size='small'
                  sx={{
                    height: 36,
                    alignSelf: "center",
                    display: "flex",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    letterSpacing: 1,
                    bgcolor: "#FFD600",
                    color: "#603F97",
                    textTransform: "uppercase",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                    cursor: "default",
                    ml: 1.5,
                    px: 1.5,
                    py: 0,
                  }}
                  data-testid='dev-env-chip'
                />
              )}
            </Stack>
          )}
        </Box>

        {/* Right Side - Profile */}
        {isAuthenticated ? (
          <Box display='flex' alignItems='center' gap={1}>
            {/* Notifications */}
            {process.env.REACT_APP_RENDER_ENV === "LOCAL" && (
              <Tooltip title='Notifications'>
                <IconButton
                  onClick={handleNotificationMenuOpen}
                  sx={{
                    color: "white",
                    mr: 1,
                    position: "relative",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  aria-label='Show notifications'
                >
                  <Badge
                    variant={unreadCount > 0 ? "dot" : undefined}
                    color='error'
                    overlap='circular'
                    invisible={unreadCount === 0}
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            <Menu
              anchorEl={notificationAnchorEl}
              open={notificationMenuOpen}
              onClose={handleNotificationMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  width: 360,
                  maxHeight: 400,
                  borderRadius: 3,
                  mt: 1.5,
                  p: 0,
                  boxShadow: 8,
                },
              }}
            >
              <Box sx={{ p: 1, pl: 2 }}>
                <Typography variant='h6' fontWeight={600}>
                  Notifications
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                {notifications.length === 0 ? (
                  <Typography sx={{ p: 2, color: "text.secondary" }}>
                    No notifications.
                  </Typography>
                ) : (
                  notifications.map((notif) => (
                    <Box
                      key={notif.id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        bgcolor: notif.isRead
                          ? "background.paper"
                          : "rgba(211,29,131,0.08)",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        position: "relative",
                      }}
                    >
                      {!notif.isRead && (
                        <CircleIcon
                          sx={{ color: "error.main", fontSize: 12, mt: 0.5 }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant='subtitle2'
                          fontWeight={notif.isRead ? 500 : 700}
                          color={notif.isRead ? "text.primary" : "primary.main"}
                          sx={{ mb: 0.2 }}
                        >
                          {notif.title}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mb: 0.5 }}
                        >
                          {notif.content}
                        </Typography>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <Chip
                            label={notif.type}
                            size='small'
                            color={
                              notif.type === "warning"
                                ? "warning"
                                : notif.type === "info"
                                  ? "info"
                                  : notif.type === "update"
                                    ? "primary"
                                    : "default"
                            }
                            sx={{ fontSize: "0.7rem", height: 18 }}
                          />
                          <Typography
                            variant='caption'
                            color='text.disabled'
                            sx={{ ml: 1 }}
                          >
                            {notif.date}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Menu>
            {/* Desktop Profile Menu */}
            {!isMobile && (
              <Tooltip title='Account settings'>
                <Button
                  onClick={handleProfileMenuOpen}
                  aria-label='Open profile menu'
                  aria-controls={profileMenuOpen ? "profile-menu" : undefined}
                  aria-haspopup='true'
                  aria-expanded={profileMenuOpen ? "true" : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    borderRadius: 3,
                    bgcolor: "rgba(255, 255, 255, 0.03)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      transform: "translateY(-1px)",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "primary.main",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    {global_authState?.name?.[0]?.toUpperCase() || "U"}
                  </Avatar>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      minWidth: 0,
                      maxWidth: 180,
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        fontSize: "0.9rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {global_authState?.name || "User"}
                    </Typography>
                    <Chip
                      icon={getUserTypeIcon(
                        global_authState?.user_type,
                        global_authState?.user_role,
                      )}
                      label={getUserTypeLabel(
                        global_authState?.user_type,
                        global_authState?.user_role,
                      )}
                      size='small'
                      color={getUserTypeColor(
                        global_authState?.user_type,
                        global_authState?.user_role,
                      )}
                      variant='filled'
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        "& .MuiChip-icon": {
                          color: "white",
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </Box>
                  <KeyboardArrowDownIcon
                    sx={{
                      color: "white",
                      transition: "transform 0.2s ease",
                      transform: profileMenuOpen
                        ? "rotate(180deg)"
                        : "rotate(0)",
                    }}
                  />
                </Button>
              </Tooltip>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Tooltip title='Menu'>
                <IconButton
                  onClick={handleMobileMenuToggle}
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Box /> // Placeholder for unauthenticated state
        )}
      </Toolbar>

      {/* Desktop Profile Menu */}
      {!isMobile && (
        <Menu
          id='profile-menu'
          anchorEl={anchorEl}
          open={profileMenuOpen}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            elevation: 8,
            sx: {
              width: 300, // 稍微收窄一点，显得更精致
              overflow: "visible",
              mt: 1.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              // 优化箭头：去掉边框，改用纯白背景和阴影，视觉更干净
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 16,
                width: 12,
                height: 12,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
                borderLeft: "none", // 移除边框
                borderTop: "none", // 移除边框
                boxShadow: "-1px -1px 0px rgba(0, 0, 0, 0.05)", // 增加极淡的左上阴影
              },
            },
          }}
          // 防止点击菜单内部时意外关闭
          disableAutoFocusItem
        >
          {/* 用户信息头部：增加极淡的背景色，与下方菜单项区分 */}
          <Box
            sx={{
              pt: 3,
              pb: 2.5,
              px: 2.5,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: "primary.main",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {global_authState?.name?.[0]?.toUpperCase() || "U"}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle1' // 改用 subtitle1 更加紧凑
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 0.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {global_authState?.name}
                </Typography>
                <Tooltip
                  title={global_authState?.email || ""}
                  placement='bottom-start'
                  arrow
                >
                  <Typography
                    variant='caption' // 改用 caption 字体
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "default",
                    }}
                  >
                    {global_authState?.email}
                  </Typography>
                </Tooltip>
              </Box>
            </Stack>

            {/* 将 Chip 独立出来放在底部，视觉上更整齐 */}
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={getUserTypeIcon(
                  global_authState?.user_type,
                  global_authState?.user_role,
                )}
                label={getUserTypeLabel(
                  global_authState?.user_type,
                  global_authState?.user_role,
                )}
                size='small'
                color={getUserTypeColor(
                  global_authState?.user_type,
                  global_authState?.user_role,
                )}
                variant='outlined'
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  "& .MuiChip-icon": { fontSize: 14 },
                }}
              />
            </Box>
          </Box>

          {/* 菜单项区域 */}
          <Box sx={{ py: 1 }}>
            <MenuItem
              onClick={() => {
                window.location.href = "/settings?tab=profile";
                handleProfileMenuClose();
              }}
              sx={{
                py: 1.5,
                px: 2.5,
                gap: 2, // 图标和文字的间距
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <PermContactCalendarIcon
                  fontSize='small'
                  sx={{ color: "text.secondary" }} // 默认用次级文字色，更低调
                />
              </ListItemIcon>
              <ListItemText
                primary='Profile'
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  color: "text.primary",
                }}
              />
            </MenuItem>

            <MenuItem
              onClick={() => {
                window.location.href = "/settings?tab=preferences";
                handleProfileMenuClose();
              }}
              sx={{
                py: 1.5,
                px: 2.5,
                gap: 2,
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <TuneIcon fontSize='small' sx={{ color: "text.secondary" }} />
              </ListItemIcon>
              <ListItemText
                primary='Preferences'
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  color: "text.primary",
                }}
              />
            </MenuItem>

            {global_authState?.user_type === "admin" && (
              <MenuItem
                onClick={() => {
                  window.location.href = "/settings?tab=billings";
                  handleProfileMenuClose();
                }}
                sx={{
                  py: 1.5,
                  px: 2.5,
                  gap: 2,
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0 }}>
                  <PaymentIcon
                    fontSize='small'
                    sx={{ color: "text.secondary" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary='Billings'
                  primaryTypographyProps={{
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    color: "text.primary",
                  }}
                />
              </MenuItem>
            )}

            {/* 退出登录增加一个上边框，与其他操作区分 */}
            <Box sx={{ px: 1, pt: 1 }}>
              <Divider />
            </Box>

            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 2.5,
                gap: 2,
                mt: 0.5,
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.06),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <LogoutIcon fontSize='small' sx={{ color: "error.main" }} />
              </ListItemIcon>
              <ListItemText
                primary='Logout'
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "error.main",
                }}
              />
            </MenuItem>
          </Box>
        </Menu>
      )}

      {/* Mobile Drawer */}
      <Drawer
        anchor='right'
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: "background.paper",
            borderLeft: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Mobile Profile Header */}
          <Box sx={{ mb: 3 }}>
            <Stack direction='row' spacing={2} alignItems='center' mb={2}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "primary.main",
                  fontSize: "1.4rem",
                  fontWeight: 600,
                }}
              >
                {global_authState?.name?.[0]?.toUpperCase() || "U"}
              </Avatar>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant='h6'
                  fontWeight={600}
                  gutterBottom
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {global_authState?.name}
                </Typography>
                <Tooltip
                  title={global_authState?.email || ""}
                  placement='bottom-start'
                  arrow
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "default",
                    }}
                  >
                    {global_authState?.email}
                  </Typography>
                </Tooltip>
                <Chip
                  icon={getUserTypeIcon(
                    global_authState?.user_type,
                    global_authState?.user_role,
                  )}
                  label={getUserTypeLabel(
                    global_authState?.user_type,
                    global_authState?.user_role,
                  )}
                  size='small'
                  color={getUserTypeColor(
                    global_authState?.user_type,
                    global_authState?.user_role,
                  )}
                />
              </Box>
            </Stack>
            <Divider />
          </Box>

          {/* Mobile Navigation */}
          <List sx={{ mb: 2 }}>
            {navigationItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() =>
                    item.external
                      ? window.open(item.path, "_blank")
                      : handleNavigation(item.path)
                  }
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    "&:hover": {
                      bgcolor: "primary.lighter",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "primary.main" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Mobile Menu Items */}
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  window.location.href = "/settings";
                  setMobileMenuOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  "&:hover": {
                    bgcolor: "primary.lighter",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "primary.main" }}>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText
                  primary='Settings'
                  primaryTypographyProps={{
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  window.location.href = "/settings?tab=preferences";
                  setMobileMenuOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  "&:hover": {
                    bgcolor: "primary.lighter",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "primary.main" }}>
                  <TuneIcon />
                </ListItemIcon>
                <ListItemText
                  primary='Preferences'
                  primaryTypographyProps={{
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ color: "error.main" }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary='Logout'
                  primaryTypographyProps={{
                    fontWeight: 500,
                    color: "error.main",
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
